import React, { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { formatUSPhone, handleUSPhoneChange } from "../../utils/phoneFormatter";
import { post_VisitorWithGroup } from "../../services/Visitor";
import { getGroupDetails } from "../../services/Group";
import baseURL from "../../config/api";

interface VisitorForm {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    wellbeingRating: number;
    prayerRequest: string;
}

interface FeelingOption {
    value: number;
    emoji: string;
    label: string;
}

interface Church {
    name: string;
    logo: string;
}

const feelingOptions: FeelingOption[] = [
    {
        value: 1,
        emoji: "😔",
        label: "Life is Bad",
    },
    {
        value: 2,
        emoji: "💔",
        label: "I'm Hurting",
    },
    {
        value: 3,
        emoji: "😐",
        label: "It Is What It Is",
    },
    {
        value: 4,
        emoji: "🙂",
        label: "I'm Good",
    },
    {
        value: 5,
        emoji: "😊",
        label: "Life is Great"
    }
];

const RequestVisitor: React.FC = () => {
    const { "*": slug } = useParams<{ "*": string }>();

    const [church, setChurch] = useState<Church | null>(null);
    const [loadingChurch, setLoadingChurch] = useState<boolean>(true);

    const [form, setForm] = useState<VisitorForm>({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        wellbeingRating: 0,
        prayerRequest: ""
    });

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState<boolean>(false);

    useEffect(() => {
        let cancelled = false;

        const fetchChurch = async () => {
            setLoadingChurch(true);
            setChurch(null);

            try {
                if (!slug) {
                    throw new Error("Church slug is missing");
                }

                const response = await getGroupDetails(encodeURIComponent(slug));

                if (cancelled) {
                    return;
                }

                if (!response) {
                    setChurch(null);
                    return;
                }

                const churchData: Church = {
                    name: response?.name ?? "",
                    logo: response?.logo ? `${baseURL}commonFile/Get?name=${response.logo}` : "",
                };

                /*
                 * If the API returns an object but has no
                 * church name, consider it invalid.
                 */
                if (!churchData.name.trim()) {
                    setChurch(null);
                    return;
                }

                setChurch(churchData);
            } catch (error) {
                if (!cancelled) {
                    console.error("Failed to load church:", error);
                    setChurch(null);
                }
            } finally {
                if (!cancelled) {
                    setLoadingChurch(false);
                }
            }
        };

        fetchChurch();

        return () => {
            cancelled = true;
        };
    }, [slug]);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value
        }));
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!church || submitting) {
            return;
        }

        const visitorData = {
            encryptedGroupId: slug,
            ...form,
            isConvertedToFamily: false
        };

        setSubmitting(true);

        await post_VisitorWithGroup(visitorData)
            .then(() => {
                setSubmitted(true);
                setSubmitting(false);
                setForm({
                    firstName: "",
                    lastName: "",
                    email: "",
                    phoneNumber: "",
                    wellbeingRating: 0,
                    prayerRequest: ""
                });

                window.scrollTo({
                    top: 0,
                    behavior: "auto"
                });
            })
            .catch((error) => {
                console.log(error);
                setSubmitting(false);
            });
    }

    if (loadingChurch) {
        return <ChurchPageSkeleton />;
    }

    if (!church) {
        return <ChurchNotFound />;
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6">
                <div className="mx-auto flex min-h-[80vh] max-w-xl items-center justify-center">
                    <div className="w-full rounded-3xl border border-slate-200 bg-white p-7 text-center shadow-xl shadow-slate-900/5 sm:p-12">
                        <div className="mb-7 flex flex-col items-center">
                            <ChurchLogo
                                church={church}
                                size="large"
                            />

                            <p className="mt-4 text-sm font-bold text-custom-blue">
                                {church.name}
                            </p>
                        </div>

                        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-secondary/10 text-3xl font-bold text-secondary">
                            ✓
                        </div>

                        <h1 className="font-serif text-4xl font-normal text-custom-blue">
                            Thank You!
                        </h1>

                        <p className="mx-auto mt-4 max-w-md text-[15px] leading-7 text-slate-500">
                            We're so glad you visited us today. It was a joy
                            to have you with us.
                        </p>

                        {
                            form.prayerRequest && (
                                <div className="mt-7 flex gap-3 rounded-2xl bg-secondary/5 p-4 text-left">
                                    <span className="text-2xl">
                                        🙏
                                    </span>

                                    <div>
                                        <p className="text-sm font-semibold text-custom-blue">
                                            We'll be praying for you
                                        </p>

                                        <p className="mt-1 text-xs leading-5 text-slate-500">
                                            Thank you for trusting us with your
                                            prayer request.
                                        </p>
                                    </div>
                                </div>
                            )
                        }

                        <button
                            type="button"
                            onClick={() => setSubmitted(false)}
                            className="mt-7 h-[52px] w-full max-w-[220px] rounded-xl bg-secondary px-6 text-sm font-bold text-white shadow-lg shadow-secondary/20 transition hover:-translate-y-0.5 hover:bg-secondary/90 active:translate-y-0"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 px-3 py-6 sm:px-5 sm:py-10">
            <div className="mx-auto w-full max-w-3xl">
                <header className="mb-10 text-center sm:mb-12">
                    <div className="flex flex-col items-center">
                        <ChurchLogo church={church} />

                        <h2 className="mt-5 text-lg font-bold tracking-tight text-custom-blue sm:text-xl">
                            {church.name}
                        </h2>

                        <div className="mt-3 flex items-center justify-center gap-3">
                            <span className="h-px w-8 bg-slate-200" />

                            <span className="h-1.5 w-1.5 rounded-full bg-secondary" />

                            <span className="h-px w-8 bg-slate-200" />
                        </div>

                        <p className="mt-6 text-[11px] font-bold tracking-[0.3em] text-secondary">
                            WELCOME
                        </p>

                        <h1 className="mt-2 font-serif text-3xl font-normal leading-tight text-custom-blue sm:text-[42px]">
                            We're Glad You're Here
                        </h1>

                        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 sm:text-base sm:leading-7">
                            Thank you for visiting us today. We'd love to get
                            to know you and stay connected.
                        </p>
                    </div>
                </header>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-900/5 sm:rounded-3xl sm:p-10">
                    <form onSubmit={handleSubmit}>
                        <section>
                            <div className="mb-6 flex items-start gap-3">
                                <SectionNumber number="01" />

                                <div>
                                    <h2 className="text-[17px] font-semibold text-slate-800">
                                        Tell us about yourself
                                    </h2>

                                    <p className="mt-0.5 text-xs leading-5 text-slate-400">
                                        We'd love to know your name.
                                    </p>
                                </div>
                            </div>

                            <div className="grid gap-0 sm:grid-cols-2 sm:gap-4">
                                <div className="mb-4">
                                    <label className="mb-2 block text-sm font-semibold text-slate-600">
                                        First Name{" "}
                                        <span className="text-red-500">
                                            *
                                        </span>
                                    </label>

                                    <input
                                        type="text"
                                        name="firstName"
                                        value={form.firstName}
                                        onChange={handleChange}
                                        placeholder="Your first name"
                                        required
                                        className="h-[50px] w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-secondary focus:bg-white focus:ring-4 focus:ring-secondary/10"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="mb-2 block text-sm font-semibold text-slate-600">
                                        Last Name{" "}
                                        <span className="text-red-500">
                                            *
                                        </span>
                                    </label>

                                    <input
                                        type="text"
                                        name="lastName"
                                        value={form.lastName}
                                        onChange={handleChange}
                                        placeholder="Your last name"
                                        required
                                        className="h-[50px] w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-secondary focus:bg-white focus:ring-4 focus:ring-secondary/10"
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="mb-2 block text-sm font-semibold text-slate-600">
                                    Email{" "}
                                    <span className="text-red-500">
                                        *
                                    </span>
                                </label>

                                <input
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="you@example.com"
                                    required
                                    className="h-[50px] w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-secondary focus:bg-white focus:ring-4 focus:ring-secondary/10"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-semibold text-slate-600">
                                    Mobile Phone
                                </label>

                                <input
                                    type="tel"
                                    name="phone"
                                    value={formatUSPhone(form.phoneNumber)}
                                    onChange={(e) => {
                                        handleUSPhoneChange(e, (digits) =>
                                            setForm((prev) => ({
                                                ...prev,
                                                phoneNumber: digits
                                            }))
                                        );
                                    }}
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    className="h-[50px] w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-secondary focus:bg-white focus:ring-4 focus:ring-secondary/10"
                                    placeholder="(555) 123-4567"
                                />
                            </div>
                        </section>

                        <Divider />

                        <section>
                            <div className="mb-6 flex items-start gap-3">
                                <SectionNumber number="02" />

                                <div>
                                    <h2 className="text-[17px] font-semibold text-slate-800">
                                        How are you doing?
                                    </h2>

                                    <p className="mt-0.5 text-xs leading-5 text-slate-400">
                                        This helps us know how we can care for
                                        you.
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                                {
                                    feelingOptions.map((item) => {
                                        const selected = form.wellbeingRating === item.value;

                                        return (
                                            <label
                                                key={item.value}
                                                className={`flex min-h-[100px] cursor-pointer flex-col items-center justify-center rounded-xl border p-2 text-center transition ${selected
                                                    ? "border-secondary bg-secondary/5 text-secondary shadow-sm"
                                                    : "border-slate-200 bg-slate-50 text-slate-500 hover:border-secondary/40"
                                                    } ${item.value === 5
                                                        ? "col-span-2 sm:col-span-1"
                                                        : ""
                                                    }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="feeling"
                                                    value={item.value}
                                                    checked={selected}
                                                    onChange={(e) => {
                                                        setForm((prev) => ({
                                                            ...prev,
                                                            wellbeingRating: Number(e.target.value),
                                                        }));
                                                    }}
                                                    className="sr-only"
                                                />

                                                <span className="text-2xl">
                                                    {item.emoji}
                                                </span>

                                                <span className="mt-2 text-[11px]">
                                                    {item.label}
                                                </span>
                                            </label>
                                        );
                                    })
                                }
                            </div>
                        </section>

                        <Divider />

                        <section>
                            <div className="mb-6 flex items-start gap-3">
                                <SectionNumber number="03" />

                                <div>
                                    <h2 className="text-[17px] font-semibold text-slate-800">
                                        How can we pray for you?
                                    </h2>

                                    <p className="mt-0.5 text-xs leading-5 text-slate-400">
                                        Share anything that's on your heart.
                                    </p>
                                </div>
                            </div>

                            <label className="mb-2 block text-sm font-semibold text-slate-600">
                                Prayer Request
                            </label>

                            <textarea
                                name="prayerRequest"
                                value={form.prayerRequest}
                                onChange={handleChange}
                                rows={5}
                                placeholder="Tell us how we can pray for you..."
                                className="w-full resize-y rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-secondary focus:bg-white focus:ring-4 focus:ring-secondary/10"
                            />
                        </section>

                        <div className="mt-8 border-t border-slate-100 pt-7 text-center">
                            <button
                                type="submit"
                                disabled={submitting}
                                className={`h-[54px] w-full rounded-xl px-6 text-sm font-bold text-white shadow-lg shadow-secondary/20 transition
                                    ${submitting
                                        ? "cursor-not-allowed bg-secondary/70"
                                        : "bg-secondary hover:-translate-y-0.5 hover:bg-secondary/90 hover:shadow-xl active:translate-y-0"
                                    }`}
                            >
                                {
                                    submitting
                                        ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                                Submitting...
                                            </span>
                                        ) : (
                                            <>
                                                Submit
                                                <span className="ml-2">→</span>
                                            </>
                                        )
                                }
                            </button>

                            <p className="mt-3 text-[11px] text-slate-400">
                                🔒 Your information is private and will only
                                be used by our church team.
                            </p>
                        </div>
                    </form>
                </div>

                <footer className="py-7 text-center">
                    <p className="font-serif text-base text-custom-blue">
                        We're praying for you. ❤️
                    </p>

                    <p className="mt-1 text-xs text-slate-400">
                        Thank you for visiting {church.name}.
                    </p>
                </footer>
            </div>
        </div>
    );
};


interface ChurchLogoProps {
    church: Church;
    size?: "default" | "large";
}

const ChurchLogo: React.FC<ChurchLogoProps> = ({ church, size = "default" }) => {
    const isLarge = size === "large";

    const [loading, setLoading] = useState<boolean>(Boolean(church.logo));
    const [error, setError] = useState<boolean>(!church.logo);

    useEffect(() => {
        setLoading(Boolean(church.logo));
        setError(!church.logo);
    }, [church.logo]);

    return (
        <div
            className={`relative ${isLarge
                ? "h-24 w-24"
                : "h-28 w-28 sm:h-32 sm:w-32"
                }`}
        >
            <div className="absolute inset-0 scale-125 rounded-full bg-secondary/10 blur-2xl" />

            <div
                className={`relative flex h-full w-full items-center justify-center rounded-md border-2 border-secondary/20 bg-white p-3 shadow-xl ${isLarge ? "shadow-secondary/10" : ""
                    }`}
            >
                <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-md bg-secondary/5">
                    {
                        loading && !error && (
                            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80">
                                <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-secondary" />
                            </div>
                        )
                    }

                    {
                        !error && church.logo ? (
                            <img
                                src={church.logo}
                                alt={`${church.name} logo`}
                                onLoad={() => setLoading(false)}
                                onError={() => {
                                    setLoading(false);
                                    setError(true);
                                }}
                                className={`h-[100%] w-[100%] object-contain transition-all duration-500 ${loading
                                    ? "scale-90 opacity-0"
                                    : "scale-100 opacity-100"
                                    }`}
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center bg-secondary/5">
                                <span
                                    className={`font-bold text-custom-blue ${isLarge
                                        ? "text-2xl"
                                        : "text-3xl"
                                        }`}
                                >
                                    {getChurchInitials(church.name)}
                                </span>
                            </div>
                        )
                    }
                </div>
            </div>

            {
                !isLarge && (
                    <>
                        <div className="absolute -right-1 top-3 h-4 w-4 rounded-full border-4 border-slate-50 bg-secondary" />

                        <div className="absolute -bottom-1 left-4 h-3 w-3 rounded-full border-2 border-slate-50 bg-custom-blue" />
                    </>
                )
            }
        </div>
    );
};

const getChurchInitials = (name: string): string => {
    const words = name
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (words.length === 0) {
        return "C";
    }

    if (words.length === 1) {
        return words[0]
            .substring(0, 2)
            .toUpperCase();
    }

    return words
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase();
}

interface SectionNumberProps {
    number: string;
}

const SectionNumber: React.FC<SectionNumberProps> = ({ number }) => {
    return (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-xs font-extrabold text-secondary">
            {number}
        </div>
    );
};

const Divider: React.FC = () => {
    return (
        <div className="my-7 h-px bg-slate-100 sm:my-8" />
    );
};

const ChurchPageSkeleton: React.FC = () => {
    return (
        <div className="min-h-screen bg-slate-50 px-3 py-6 sm:px-5 sm:py-10">
            <div className="mx-auto w-full max-w-3xl">
                <div className="mb-10 text-center sm:mb-12">
                    <div className="flex justify-center">
                        <div className="h-28 w-28 animate-pulse rounded-full bg-slate-200 sm:h-32 sm:w-32" />
                    </div>

                    <div className="mx-auto mt-5 h-5 w-48 animate-pulse rounded-full bg-slate-200" />

                    <div className="mx-auto mt-4 h-1 w-16 animate-pulse rounded-full bg-slate-200" />

                    <div className="mx-auto mt-7 h-3 w-20 animate-pulse rounded bg-slate-200" />

                    <div className="mx-auto mt-4 h-10 w-72 animate-pulse rounded-lg bg-slate-200 sm:w-96" />

                    <div className="mx-auto mt-4 h-4 w-full max-w-md animate-pulse rounded bg-slate-200" />

                    <div className="mx-auto mt-2 h-4 w-72 animate-pulse rounded bg-slate-200" />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-900/5 sm:rounded-3xl sm:p-10">

                    <div className="flex gap-3">
                        <div className="h-9 w-9 shrink-0 animate-pulse rounded-xl bg-slate-200" />

                        <div className="flex-1">
                            <div className="h-5 w-52 animate-pulse rounded bg-slate-200" />

                            <div className="mt-2 h-3 w-40 animate-pulse rounded bg-slate-100" />
                        </div>
                    </div>

                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                        <SkeletonInput />
                        <SkeletonInput />
                    </div>

                    <div className="mt-4">
                        <SkeletonInput />
                    </div>

                    <div className="mt-4">
                        <SkeletonInput />
                    </div>

                    <div className="my-8 h-px bg-slate-100" />

                    <div className="flex gap-3">
                        <div className="h-9 w-9 shrink-0 animate-pulse rounded-xl bg-slate-200" />

                        <div>
                            <div className="h-5 w-48 animate-pulse rounded bg-slate-200" />

                            <div className="mt-2 h-3 w-60 animate-pulse rounded bg-slate-100" />
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-5">
                        {
                            [1, 2, 3, 4, 5].map((item) => (
                                <div
                                    key={item}
                                    className="h-[100px] animate-pulse rounded-xl bg-slate-100"
                                />
                            ))
                        }
                    </div>

                    <div className="my-8 h-px bg-slate-100" />

                    <div className="flex gap-3">
                        <div className="h-9 w-9 shrink-0 animate-pulse rounded-xl bg-slate-200" />

                        <div>
                            <div className="h-5 w-56 animate-pulse rounded bg-slate-200" />

                            <div className="mt-2 h-3 w-60 animate-pulse rounded bg-slate-100" />
                        </div>
                    </div>

                    <div className="mt-6 h-32 animate-pulse rounded-xl bg-slate-100" />

                    <div className="mt-8 h-[54px] animate-pulse rounded-xl bg-slate-200" />
                </div>

                <div className="py-7 text-center">
                    <div className="mx-auto h-5 w-48 animate-pulse rounded bg-slate-200" />

                    <div className="mx-auto mt-2 h-3 w-56 animate-pulse rounded bg-slate-100" />
                </div>
            </div>
        </div>
    );
};

const SkeletonInput: React.FC = () => {
    return (
        <div>
            <div className="mb-2 h-3 w-24 animate-pulse rounded bg-slate-200" />

            <div className="h-[50px] animate-pulse rounded-xl bg-slate-100" />
        </div>
    );
};

const ChurchNotFound: React.FC = () => {
    return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5">
            <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl shadow-slate-900/5 sm:p-10">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-50 text-3xl font-bold text-red-500">
                    !
                </div>

                <h1 className="mt-6 text-2xl font-bold text-custom-blue">
                    Church Not Found
                </h1>

                <p className="mt-3 text-sm leading-6 text-slate-500">
                    We couldn't find the church associated with this
                    visitor link. Please check the QR code and try again.
                </p>

                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="mt-7 h-12 w-full rounded-xl bg-custom-blue px-5 text-sm font-bold text-white transition hover:bg-custom-blue/90"
                >
                    Try Again
                </button>
            </div>
        </div>
    );
};

export default RequestVisitor;
